import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePagination, useSortBy, useTable } from "react-table";
import moment from "moment";
import { MdOutlineEdit } from "react-icons/md";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import Loader from "../../../../reusableUI/loader";
import Page from "../../../../reusableUI/page";
import PatientHistory from "../partnerPortal/patientHistory";
import { syncSelectedPatientFromList } from "./patientSelectionState";
import AddPatientModal from "./AddPatientModal";
import { showToast } from "../../../../reusableUI/toaster";
import { CommonImages } from "../../../../reusableUI/images";
import { CheckCircle2, Minus } from "lucide-react";

import {
  getPartnerPatients,
  getStandByStaffs,
  createVisitForPatient,
  getPartner,
  addPatient,
  updatePatient,
  uploadDischargeSummary,
  getPatientDischargeSummaries,
} from "../../../../Service/partnerAPI";
import { getLookupData } from "../../../../Service/lookup";
import { formatPhoneNumber } from "../../../../utils/Functions";

/* ---------------- Small helpers ---------------- */

const SortIcon = ({ isSorted, isSortedDesc }) => {
  if (!isSorted) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 12l5 5 5-5H5zm10-4L10 3 5 8h10z" />
      </svg>
    );
  }
  if (isSortedDesc) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-blue-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5 5 5-5H5z" />
      </svg>
    );
  }
  return (
    <svg
      className="h-3 w-3 ml-1 text-blue-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M15 12l-5-5-5 5h10z" />
    </svg>
  );
};

const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "string") return id;
  if (typeof id === "object" && id.$oid) return String(id.$oid);
  return String(id);
};

const capitalizeFirstLetter = (str = "") => {
  const s = String(str || "");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "-";
};

const safeArr = (v) => (Array.isArray(v) ? v : []);

const getPatientName = (p) =>
  p?.personalInfo?.basicInfo?.nameInfo?.fullName ||
  p?.personalInfo?.basicInfo?.nameInfo?.name ||
  p?.name ||
  "N/A";

const getPatientTitle = (p) =>
  p?.personalInfo?.basicInfo?.nameInfo?.title || p?.title || "";

const getPatientNameWithTitle = (p) => {
  const title = String(getPatientTitle(p) || "").trim();
  const full = String(
    p?.personalInfo?.basicInfo?.nameInfo?.fullName || getPatientName(p) || "",
  ).trim();
  if (!full) return "N/A";
  return title ? `${title}. ${full}` : full;
};

const getGender = (p) =>
  p?.personalInfo?.basicInfo?.otherInfo?.gender ||
  p?.personalInfo?.basicInfo?.gender ||
  p?.gender ||
  "";

const getDob = (p) =>
  p?.personalInfo?.basicInfo?.otherInfo?.dob ||
  p?.personalInfo?.basicInfo?.dob ||
  p?.dob ||
  null;

const calculateAge = (dob) => {
  if (!dob) return "-";
  const m = moment(dob);
  if (!m.isValid()) return "-";
  return `${moment().diff(m, "years")} yrs`;
};

const getLocation = (p) => {
  const city =
    p?.personalInfo?.addressInfo?.city ||
    p?.personalInfo?.basicInfo?.addressInfo?.city ||
    p?.addressInfo?.city ||
    p?.city ||
    "";
  const district =
    p?.personalInfo?.addressInfo?.district ||
    p?.personalInfo?.basicInfo?.addressInfo?.district ||
    p?.addressInfo?.district ||
    p?.district ||
    "";
  const area =
    p?.personalInfo?.addressInfo?.area ||
    p?.personalInfo?.basicInfo?.addressInfo?.area ||
    "";

  const cleanCity = String(city || "").trim();
  const cleanDistrict = String(district || "").trim();
  const cleanArea = String(area || "").trim();

  if (cleanCity && cleanDistrict) {
    if (cleanCity.toLowerCase() === cleanDistrict.toLowerCase()) {
      return capitalizeFirstLetter(cleanCity);
    }
    return `${capitalizeFirstLetter(cleanCity)}, ${capitalizeFirstLetter(cleanDistrict)}`;
  }
  if (cleanCity) return capitalizeFirstLetter(cleanCity);
  if (cleanDistrict) return capitalizeFirstLetter(cleanDistrict);
  if (cleanArea) return capitalizeFirstLetter(cleanArea);
  return "-";
};

const getStaffDisplayName = (staff) => {
  if (!staff) return "";
  const info = staff?.personalInfo?.basicInfo?.nameInfo || {};
  const title = info?.title || "";
  const fullName = info?.fullName || "";
  const built = `${title ? title + ". " : ""}${fullName || ""}`.trim();
  if (built) return built;
  return String(staff?.name || staff?.fullName || "").trim();
};

const getStaffGender = (staff) =>
  staff?.personalInfo?.basicInfo?.otherInfo?.gender ||
  staff?.personalInfo?.basicInfo?.gender ||
  staff?.gender ||
  "";

/* ---------- New helpers for requested table columns ---------- */

const getPatientDisplayId = (p) =>
  p?.mrn ||
  p?.patientID ||
  p?.patientId ||
  p?.patientNo ||
  p?.patientNumber ||
  p?.uhid ||
  "";

const getProfileCompletionCount = (patient) => {
  const checks = [
    patient?.medicalHistory?.completed === true,
    patient?.preExistingConditions?.completed === true,
    patient?.allergies?.completed === true,
    patient?.familyHistory?.completed === true,
    patient?.personalInfo?.completed === true,
  ];
  return checks.filter(Boolean).length;
};

const hasDischargeSummary = (patient) =>
  Array.isArray(patient?.dischargeSummaries) &&
  patient.dischargeSummaries.length > 0;

const isClinicalProfileComplete = (patient) =>
  patient?.medicalHistory?.completed === true &&
  patient?.preExistingConditions?.completed === true &&
  patient?.allergies?.completed === true &&
  patient?.familyHistory?.completed === true &&
  patient?.personalInfo?.completed === true;

const isDischargeProfileComplete = (patient) =>
  hasDischargeSummary(patient) && patient?.personalInfo?.completed === true;

const isPatientProfileComplete = (patient) =>
  isDischargeProfileComplete(patient) || isClinicalProfileComplete(patient);

const isPersonalInfoCompleted = (patient) =>
  patient?.personalInfo?.completed === true;

const getProfileStatus = (patient) => {
  if (isPatientProfileComplete(patient)) return "Completed";
  const completedCount = getProfileCompletionCount(patient);
  return `${completedCount}/5`;
};

const getOnboardedOnRaw = (p) =>
  p?.createdAt ||
  p?.createdOn ||
  p?.createdDate ||
  p?.onboardedOn ||
  p?.onBoardedOn ||
  p?.registeredAt ||
  p?.created_at ||
  null;

const formatOnboardedOn = (p) => {
  const d = getOnboardedOnRaw(p);
  if (!d) return "-";
  const m = moment(d);
  return m.isValid() ? m.format("DD MMM YYYY") : "-";
};

const getLastPrescribedRaw = (p) =>
  p?.lastPrescribedAt ||
  p?.lastPrescriptionAt ||
  p?.lastPrescribedOn ||
  p?.lastPrescriptionOn ||
  null;


/* ---------- Build services array for reusable PatientHistory ---------- */

const extractServiceNames = (rawPatient) => {
  if (!rawPatient) return [];
  const s = rawPatient?.serviceInfo || {};
  const buckets = [
    "regularServices",
    "oneTimeServices",
    "additionalServices",
    "physioServices",
  ];

  const names = [];
  buckets.forEach((k) => {
    safeArr(s?.[k]).forEach((item) => {
      if (typeof item === "string") {
        if (item.trim()) names.push(item.trim());
        return;
      }
      const nm = item?.name || item?.serviceType || item?.label;
      if (nm) names.push(String(nm));
    });
  });

  return Array.from(new Set(names));
};

const normalizeForReusablePatientHistory = (rawPatient) => {
  if (!rawPatient) return null;
  return {
    patient: {
      name: getPatientName(rawPatient),
      phone: rawPatient?.phone || "",
    },
    patientInfo: rawPatient,
    patientListDetails: {
      mrn: getPatientDisplayId(rawPatient),
      lastPrescribedAt: getLastPrescribedRaw(rawPatient),
    },
    visitInfo: { services: extractServiceNames(rawPatient) },
  };
};

/* ---------------- Duty helpers ---------------- */

const SPECIALIST_OPTIONS = [
  { value: "physiotherapist", label: "Physiotherapist" },
  { value: "nurse", label: "Nurse" },
  { value: "nursing-assisstant", label: "Nursing Assistant" },
];

const mapRoleForStandbyApi = (role) => role;

const buildPhysioSlots_6am_to_11pm = () => {
  const slots = [];
  for (let h = 6; h < 23; h++) {
    const start = moment({ hour: h, minute: 0 });
    const end = moment({ hour: h + 1, minute: 0 });
    slots.push(`${start.format("hh:mm A")} to ${end.format("hh:mm A")}`);
  }
  return slots;
};

const getLookupValue = (item) =>
  String(
    item?.value ?? item?.label ?? item?.name ?? item?.title ?? item?.text ?? "",
  );

const shiftLabel = (shift) => {
  if (shift === "morning") return "Morning";
  if (shift === "night") return "Night";
  if (shift === "24/7") return "24/7";
  return shift || "-";
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const parseSelectedSlot = (slotStr = "") => {
  const s = String(slotStr || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return { startTimeStr: "", endTimeStr: "" };

  const parts = s.split(/\s*(?:to|-|–|—)\s*/i);
  if (parts.length < 2) return { startTimeStr: "", endTimeStr: "" };

  const normalize = (t) =>
    String(t || "")
      .replace(/\s+/g, " ")
      .replace(/(\d)(AM|PM)$/i, "$1 $2")
      .trim();

  return {
    startTimeStr: normalize(parts[0]),
    endTimeStr: normalize(parts[1]),
  };
};

const dutyKey = (shift, type) => `${shift}__${type}`;

/* ---------------- UI ---------------- */

const inputClass =
  "w-full bg-[#F6F8FC] rounded-xl px-3 py-2.5 text-[12px] font-poppins border border-gray-200 outline-none focus:border-[#3E57E8] focus:ring-2 focus:ring-[#3E57E8]/15";

const FieldTopLabel = ({ label, required, error, hint, children }) => {
  return (
    <div className="flex flex-col">
      <div className="mb-1 flex items-center gap-1">
        <span className="text-[12px] font-medium text-gray-800 font-poppins">
          {label}
        </span>
        {required ? <span className="text-[12px] text-red-600">*</span> : null}
      </div>
      {children}
      {hint ? (
        <div className="text-[10px] text-gray-400 font-poppins mt-1">
          {hint}
        </div>
      ) : null}
      {error ? (
        <div className="text-[11px] text-red-600 font-poppins mt-1">
          {error}
        </div>
      ) : null}
    </div>
  );
};

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#F6F8FC",
    borderRadius: "0.75rem",
    minHeight: 40,
    borderColor: state.isFocused ? "#3E57E8" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(62,87,232,0.15)" : "none",
    ":hover": { borderColor: "#3E57E8" },
    fontSize: 12,
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
  placeholder: (base) => ({ ...base, fontSize: 12, color: "#9CA3AF" }),
  singleValue: (base) => ({ ...base, fontSize: 12 }),
  multiValue: (base) => ({ ...base, backgroundColor: "#E7EEFF" }),
  multiValueLabel: (base) => ({ ...base, fontSize: 12, color: "#1F2937" }),
  multiValueRemove: (base) => ({
    ...base,
    ":hover": { backgroundColor: "#C7D2FE", color: "#111827" },
  }),
  menu: (base) => ({ ...base, zIndex: 30, fontSize: 12 }),
};

/* ---------------- Component ---------------- */

const PatientsBooking = ({ searchTerm }) => {
  const navigate = useNavigate();

  const [patientList, setPatientList] = useState([]);
  const [selectedPatientRaw, setSelectedPatientRaw] = useState(null);
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);
  const [patientModalMode, setPatientModalMode] = useState("add");
  const [patientModalTarget, setPatientModalTarget] = useState(null);
  const [dischargeSummaries, setDischargeSummaries] = useState([]);
  const [dischargeSummariesLoading, setDischargeSummariesLoading] =
    useState(false);
  const [dischargeSummariesError, setDischargeSummariesError] = useState("");
  const dischargeFetchSeq = useRef(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paginationPage, setPaginationPage] = useState(0);
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const savedPageCount = localStorage.getItem("patientsBookingPageCount");
    return savedPageCount ? parseInt(savedPageCount, 10) : 25;
  });

  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState("");
  const [assignStaffList, setAssignStaffList] = useState([]);

  const [casedata, setCase] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [pricing, setPricing] = useState({
    nurseRegularSlots: [],
    nurseOneTimeServices: [],
    physioAssessmentFee: 0,
    physioTreatments: [],
  });

  // ✅ NEW: pricing gate modal (no auto redirect)
  const [pricingGate, setPricingGate] = useState({
    open: false,
    message:
      "Please configure the service and pricing to proceed with patient onboarding.",
  });

  // ✅ NEW: pricing configured check (any fee configured)
  const isPricingConfigured = useMemo(() => {
    const hasNurseRegular = safeArr(pricing?.nurseRegularSlots).some(
      (x) => toNumber(x?.fee) > 0,
    );
    const hasNurseOneTime = safeArr(pricing?.nurseOneTimeServices).some(
      (x) => toNumber(x?.fee) > 0,
    );
    const hasPhysioAssessment = toNumber(pricing?.physioAssessmentFee) > 0;
    const hasPhysioTreatments = safeArr(pricing?.physioTreatments).some(
      (x) => toNumber(x?.fee) > 0,
    );
    return (
      hasNurseRegular ||
      hasNurseOneTime ||
      hasPhysioAssessment ||
      hasPhysioTreatments
    );
  }, [pricing]);

  // ✅ NEW: guard (shows modal note instead of auto navigation)
  const ensurePricingOrPrompt = useCallback(() => {
    if (pricingLoading) {
      showToast("Pricing is loading, please wait...", "Warning");
      return false;
    }
    if (pricingError) {
      showToast(pricingError || "Failed to load pricing.", "Error");
      return false;
    }
    if (!isPricingConfigured) {
      setPricingGate({
        open: true,
        message:
          "Please configure the service and pricing to proceed with patient onboarding.",
      });
      return false;
    }
    return true;
  }, [pricingLoading, pricingError, isPricingConfigured]);

  const [formErrors, setFormErrors] = useState({});
  const [assigning, setAssigning] = useState(false);

  const getFreshInitialDutyForm = () => ({
    specialistType: "",
    nurseServiceType: "",
    nurseRegularDutyKey: "",
    serviceJson: [],
    requiresAssessment: false,
    assessmentStaffId: "",
    dutyStaffId: "",
    standbyStaffId: "",
    date: "",
    days: "",
    slot: "",
  });

  const [dutyForm, setDutyForm] = useState(() => getFreshInitialDutyForm());

  const isNurseLike =
    dutyForm.specialistType === "nurse" ||
    dutyForm.specialistType === "nursing-assisstant";
  const isPhysio = dutyForm.specialistType === "physiotherapist";

  /* ---------------- Fetch patients ---------------- */

  const fetchPatients = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError("");
      const res = await getPartnerPatients();

      if (res?.error === false) {
        const nextPatients = Array.isArray(res?.data) ? res.data : [];
        setPatientList(nextPatients);
        setSelectedPatientRaw((prev) =>
          syncSelectedPatientFromList(prev, nextPatients),
        );
      } else {
        setPatientList([]);
        setError(res?.message || "Failed to fetch patients");
      }
    } catch (e) {
      console.error("getPartnerPatients error:", e);
      setPatientList([]);
      setError("Something went wrong while fetching patients");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /* ---------------- Fetch lookup data ---------------- */

  const getCase = async () => {
    try {
      setLookupLoading(true);
      setLookupError("");
      const result = await getLookupData();
      if (!result?.error)
        setCase(Array.isArray(result?.data) ? result.data : []);
      else {
        setCase([]);
        setLookupError(result?.message || "Failed to load lookup data");
      }
    } catch (e) {
      console.error("getLookupData error:", e);
      setCase([]);
      setLookupError("Failed to load lookup data");
    } finally {
      setLookupLoading(false);
    }
  };

  /* ---------------- Fetch partner pricing ---------------- */

  const fetchPricing = async () => {
    try {
      setPricingLoading(true);
      setPricingError("");

      const res = await getPartner();
      if (!res || res?.error) {
        setPricingError(res?.message || "Failed to fetch pricing");
        return;
      }

      const data = res?.data || {};
      const regularSlots = safeArr(
        data?.pricingInfo?.nurseService?.regularShifts?.slots,
      ).map((x) => ({
        shift: x?.shift,
        type: x?.type,
        fee: toNumber(x?.fee ?? x?.fees),
      }));

      const oneTimeServices = safeArr(
        data?.pricingInfo?.nurseService?.oneTimeShifts?.services,
      ).map((x) => ({
        serviceType: x?.serviceType,
        fee: toNumber(x?.fee ?? x?.fees),
      }));

      const physioNode =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.physio_service ||
        {};

      const physioAssessmentFee = toNumber(
        physioNode?.assessmentFees ??
          physioNode?.assessmentFee ??
          physioNode?.assessment_fees ??
          0,
      );

      const physioTreatments = safeArr(physioNode?.services)
        .map((x) => ({
          serviceType: x?.name || x?.serviceType || x?.label || "",
          fee: toNumber(x?.fee ?? x?.fees),
        }))
        .filter((x) => x.serviceType);

      setPricing({
        nurseRegularSlots: regularSlots.filter(
          (x) => x.shift && x.type && x.fee > 0,
        ),
        nurseOneTimeServices: oneTimeServices.filter(
          (x) => x.serviceType && x.fee > 0,
        ),
        physioAssessmentFee,
        physioTreatments: physioTreatments.filter((x) => x.fee > 0),
      });
    } catch (e) {
      console.error("getPartner pricing error:", e);
      setPricingError("Failed to fetch pricing");
    } finally {
      setPricingLoading(false);
    }
  };

  /* ---------------- Fetch staff list ---------------- */

  const fetchStandByStaffs = async (role, dateStr) => {
    try {
      if (!role || !dateStr) return;

      setStaffLoading(true);
      setStaffError("");

      const dateObj = moment(dateStr, "YYYY-MM-DD", true).toDate();
      const res = await getStandByStaffs(mapRoleForStandbyApi(role), dateObj);

      if (res?.data)
        setAssignStaffList(Array.isArray(res.data) ? res.data : []);
      else setAssignStaffList([]);
    } catch (e) {
      console.error("getStandByStaffs error:", e);
      setAssignStaffList([]);
      setStaffError("Failed to fetch staff list");
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
    getCase();
    fetchPricing();
  }, []);

  useEffect(() => {
    if (!selectedPatientRaw) return;
    if (!dutyForm.specialistType || !dutyForm.date) return;
    fetchStandByStaffs(dutyForm.specialistType, dutyForm.date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientRaw, dutyForm.specialistType, dutyForm.date]);

  /* ---------------- Nurse regular duty options ---------------- */

  const nurseRegularDutyOptions = useMemo(() => {
    return (pricing.nurseRegularSlots || []).map((x) => ({
      key: dutyKey(x.shift, x.type),
      label: `${x.type} Duty - ${shiftLabel(x.shift)}`,
      shift: x.shift,
      type: x.type,
      fee: x.fee,
    }));
  }, [pricing.nurseRegularSlots]);

  const selectedRegularDuty = useMemo(() => {
    if (!dutyForm.nurseRegularDutyKey) return null;
    const found = nurseRegularDutyOptions.find(
      (o) => o.key === dutyForm.nurseRegularDutyKey,
    );
    return found || null;
  }, [dutyForm.nurseRegularDutyKey, nurseRegularDutyOptions]);

  /* ---------------- Service options ---------------- */

  const serviceOptions = useMemo(() => {
    if (isPhysio && dutyForm.requiresAssessment) {
      return pricing.physioAssessmentFee > 0
        ? [
            {
              value: "Assessment",
              label: "Assessment",
              fee: pricing.physioAssessmentFee,
            },
          ]
        : [{ value: "Assessment", label: "Assessment", fee: 0 }];
    }

    if (isPhysio && !dutyForm.requiresAssessment) {
      return (pricing.physioTreatments || []).map((x) => ({
        value: x.serviceType,
        label: x.serviceType,
        fee: x.fee,
      }));
    }

    if (isNurseLike) {
      if (dutyForm.nurseServiceType === "onetime") {
        return (pricing.nurseOneTimeServices || []).map((x) => ({
          value: x.serviceType,
          label: x.serviceType,
          fee: x.fee,
        }));
      }
      return [];
    }

    return [];
  }, [
    isPhysio,
    isNurseLike,
    dutyForm.requiresAssessment,
    dutyForm.nurseServiceType,
    pricing.physioAssessmentFee,
    pricing.physioTreatments,
    pricing.nurseOneTimeServices,
  ]);

  const getFeeForService = (name) => {
    const n = String(name || "").trim();
    if (!n) return 0;

    if (isPhysio) {
      if (n.toLowerCase() === "assessment")
        return toNumber(pricing.physioAssessmentFee);
      const found = (pricing.physioTreatments || []).find(
        (x) => x.serviceType === n,
      );
      return toNumber(found?.fee);
    }

    if (isNurseLike) {
      const found = (pricing.nurseOneTimeServices || []).find(
        (x) => x.serviceType === n,
      );
      return toNumber(found?.fee);
    }

    return 0;
  };

  const computedServicesForPayload = useMemo(() => {
    if (isPhysio && dutyForm.requiresAssessment) {
      return [
        { name: "Assessment", fee: toNumber(pricing.physioAssessmentFee) },
      ];
    }

    if (isPhysio && !dutyForm.requiresAssessment) {
      return (dutyForm.serviceJson || []).map((s) => ({
        name: s,
        fee: getFeeForService(s),
      }));
    }

    if (isNurseLike && dutyForm.nurseServiceType === "onetime") {
      return (dutyForm.serviceJson || []).map((s) => ({
        name: s,
        fee: getFeeForService(s),
      }));
    }

    if (
      isNurseLike &&
      dutyForm.nurseServiceType === "regular" &&
      selectedRegularDuty
    ) {
      return [
        {
          name: selectedRegularDuty.label,
          fee: toNumber(selectedRegularDuty.fee),
        },
      ];
    }

    return [];
  }, [
    isPhysio,
    isNurseLike,
    dutyForm.requiresAssessment,
    dutyForm.serviceJson,
    dutyForm.nurseServiceType,
    selectedRegularDuty,
    pricing.physioAssessmentFee,
    pricing.physioTreatments,
    pricing.nurseOneTimeServices,
  ]);

  /* ---------------- Slots ---------------- */

  const oneHourSlots = useMemo(() => buildPhysioSlots_6am_to_11pm(), []);

  const selectedShiftDataKeys = useMemo(() => {
    if (
      !isNurseLike ||
      dutyForm.nurseServiceType !== "regular" ||
      !selectedRegularDuty
    )
      return [];

    const { shift, type } = selectedRegularDuty;

    if (shift === "24/7" || type === "24H") return ["24/7"];
    if (shift === "morning" && type === "9H") return ["morningShift9Data"];
    if (shift === "morning" && type === "12H")
      return ["morningShift12Data", "morningSHift12Data"];
    if (shift === "night" && type === "12H") return ["nightShift12Data"];

    return [];
  }, [isNurseLike, dutyForm.nurseServiceType, selectedRegularDuty]);

  const caseList = useMemo(() => {
    if (!selectedShiftDataKeys.length) return [];
    return safeArr(casedata).filter((item) =>
      selectedShiftDataKeys.includes(item?.type),
    );
  }, [casedata, selectedShiftDataKeys]);

  const slotOptions = useMemo(() => {
    if (isPhysio) return oneHourSlots;

    if (isNurseLike) {
      if (dutyForm.nurseServiceType === "regular") {
        const fromLookup = caseList.map(getLookupValue).filter(Boolean);
        return fromLookup.length ? fromLookup : [];
      }
      if (dutyForm.nurseServiceType === "onetime") return oneHourSlots;
    }

    return [];
  }, [
    isPhysio,
    isNurseLike,
    oneHourSlots,
    dutyForm.nurseServiceType,
    caseList,
  ]);

  /* ---------------- Form change handlers ---------------- */

  const onChangeSpecialist = (val) => {
    setFormErrors({});
    setDutyForm(() => ({
      ...getFreshInitialDutyForm(),
      specialistType: val,
    }));
    setAssignStaffList([]);
    setStaffError("");
  };

  const onChangeNurseServiceType = (val) => {
    setFormErrors((prev) => ({
      ...prev,
      nurseServiceType: "",
      serviceJson: "",
      nurseRegularDutyKey: "",
    }));
    setDutyForm((p) => ({
      ...p,
      nurseServiceType: val,
      nurseRegularDutyKey: "",
      serviceJson: [],
      slot: "",
      days: "",
    }));
  };

  const onChangeServiceMulti = (selectedOptions) => {
    setFormErrors((prev) => ({ ...prev, serviceJson: "" }));
    const values = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setDutyForm((p) => ({ ...p, serviceJson: values }));
  };

  const onChangeRequiresAssessment = (yes) => {
    setFormErrors((prev) => ({
      ...prev,
      serviceJson: "",
      days: "",
      assessmentStaffId: "",
    }));

    setDutyForm((p) => {
      if (yes) {
        return {
          ...p,
          requiresAssessment: true,
          serviceJson: ["Assessment"],
          days: "1",
          assessmentStaffId: "",
          slot: "",
        };
      }
      return {
        ...p,
        requiresAssessment: false,
        serviceJson: [],
        days: "",
        assessmentStaffId: "",
        slot: "",
      };
    });
  };

  /* ---------------- Validations ---------------- */

  const validateForm = () => {
    const e = {};
    const minDateStr = moment().add(1, "day").format("YYYY-MM-DD");

    if (!dutyForm.specialistType) e.specialistType = "Specialist is required";

    if (isNurseLike && !dutyForm.nurseServiceType) {
      e.nurseServiceType = "Service type is required";
    }

    if (!dutyForm.date) {
      e.date = "Date is required";
    } else {
      const m = moment(dutyForm.date, "YYYY-MM-DD", true);
      if (!m.isValid()) e.date = "Invalid date";
      else if (m.isBefore(moment(minDateStr, "YYYY-MM-DD", true), "day")) {
        e.date = "Start date must be from tomorrow onwards";
      }
    }

    if (isPhysio && dutyForm.requiresAssessment) {
      if (String(dutyForm.days) !== "1")
        e.days = "Days must be 1 for assessment";
    } else {
      const daysNum = Number(dutyForm.days);
      if (!dutyForm.days || !Number.isFinite(daysNum) || daysNum <= 0) {
        e.days = "Enter valid days (must be > 0)";
      }
    }

    if (isPhysio) {
      if (!dutyForm.requiresAssessment && dutyForm.serviceJson.length === 0) {
        e.serviceJson = "Select at least one service";
      }
      if (dutyForm.requiresAssessment && !dutyForm.assessmentStaffId) {
        e.assessmentStaffId = "Select assessment staff";
      }
    }

    if (isNurseLike) {
      if (
        dutyForm.nurseServiceType === "onetime" &&
        dutyForm.serviceJson.length === 0
      ) {
        e.serviceJson = "Select at least one service";
      }
      if (
        dutyForm.nurseServiceType === "regular" &&
        !dutyForm.nurseRegularDutyKey
      ) {
        e.nurseRegularDutyKey = "Select a regular duty type";
      }
    }

    if (!dutyForm.dutyStaffId) e.dutyStaffId = "Select duty staff";
    if (!dutyForm.standbyStaffId) e.standbyStaffId = "Select standby staff";

    if (
      dutyForm.dutyStaffId &&
      dutyForm.standbyStaffId &&
      dutyForm.dutyStaffId === dutyForm.standbyStaffId
    ) {
      e.standbyStaffId = "Standby staff must be different from duty staff";
    }

    if (!dutyForm.slot) e.slot = "Select time slot";

    return e;
  };

  const handleCancel = () => {
    setDutyForm(getFreshInitialDutyForm());
    setFormErrors({});
    setStaffError("");
    setAssignStaffList([]);
  };

  /* ---------------- Assign => createVisitForPatient(payload) ---------------- */

  const getPatientIdForPayload = (p) =>
    normalizeId(
      p?.patientID || p?.patientId || p?.userID || p?.userId || p?._id,
    );

  const handleAssign = async () => {
    const e = validateForm();
    setFormErrors(e);
    if (Object.keys(e).length > 0) return;

    if (!selectedPatientRaw) {
      window.alert("Patient not selected");
      return;
    }

    const dutyStaffDoc =
      assignStaffList.find(
        (s) => String(s?._id) === String(dutyForm.dutyStaffId),
      ) || null;

    const standbyStaffDoc =
      assignStaffList.find(
        (s) => String(s?._id) === String(dutyForm.standbyStaffId),
      ) || null;

    const assessmentStaffDoc =
      isPhysio && dutyForm.requiresAssessment
        ? assignStaffList.find(
            (s) => String(s?._id) === String(dutyForm.assessmentStaffId),
          ) || null
        : null;

    const dateStr = moment(dutyForm.date, "YYYY-MM-DD", true).format(
      "YYYY-MM-DD",
    );
    const daysNum =
      isPhysio && dutyForm.requiresAssessment ? 1 : Number(dutyForm.days);

    const { startTimeStr, endTimeStr } = parseSelectedSlot(dutyForm.slot);
    if (!startTimeStr || !endTimeStr) {
      setFormErrors((prev) => ({
        ...prev,
        slot: "Invalid time slot selected",
      }));
      return;
    }

    const startT = moment(startTimeStr, ["hh:mm A", "h:mm A"], true);
    const endT = moment(endTimeStr, ["hh:mm A", "h:mm A"], true);

    if (!startT.isValid() || !endT.isValid()) {
      setFormErrors((prev) => ({
        ...prev,
        slot: "Invalid time slot selected",
      }));
      return;
    }

    const isOvernight = endT.isSameOrBefore(startT);
    if (isPhysio && dutyForm.requiresAssessment && isOvernight) {
      setFormErrors((prev) => ({
        ...prev,
        slot: "Assessment slot cannot be overnight",
      }));
      return;
    }

    const startDate = moment(dateStr).utc().format();
    const endDate =
      isPhysio && dutyForm.requiresAssessment
        ? moment(dateStr).utc().format()
        : moment(dateStr, "YYYY-MM-DD", true)
            .add(daysNum, "day")
            .utc()
            .format();

    const services = computedServicesForPayload.map((x) => ({
      service: x.name,
      fees: toNumber(x.fee),
    }));

    const feesPerDay = services.reduce((sum, x) => sum + toNumber(x.fees), 0);
    const patientId = getPatientIdForPayload(selectedPatientRaw);

    const payload = {
      ifAssessment: !!(isPhysio && dutyForm.requiresAssessment),
      date: dateStr,

      patient: {
        name: getPatientNameWithTitle(selectedPatientRaw),
        gender: String(getGender(selectedPatientRaw) || "").toLowerCase() || "",
        type: selectedPatientRaw?.type || "primary",
        relationship: selectedPatientRaw?.relationship || "Self",
        userID: patientId,
        patientID: patientId,
        profilePicture:
          selectedPatientRaw?.profilePicture ||
          selectedPatientRaw?.personalInfo?.basicInfo?.profilePicture ||
          selectedPatientRaw?.personalInfo?.basicInfo?.profilePic ||
          "",
        esign: selectedPatientRaw?.esign || {},
      },

      specialistInfo: {
        userID: normalizeId(dutyForm.dutyStaffId),
        name: dutyStaffDoc ? getStaffDisplayName(dutyStaffDoc) : "",
        role: dutyForm.specialistType,
        subRole: dutyForm.specialistType,
        gender: String(getStaffGender(dutyStaffDoc) || "").toLowerCase() || "",
        esign: dutyStaffDoc?.esign || [],
      },

      addressInfo: selectedPatientRaw?.personalInfo?.addressInfo,

      dutyStaff: {
        userID: normalizeId(dutyForm.dutyStaffId),
        name: dutyStaffDoc ? getStaffDisplayName(dutyStaffDoc) : "",
      },

      standBySpecialist: {
        userID: normalizeId(dutyForm.standbyStaffId),
        name: standbyStaffDoc ? getStaffDisplayName(standbyStaffDoc) : "",
      },

      assessmentStaff:
        isPhysio && dutyForm.requiresAssessment && dutyForm.assessmentStaffId
          ? {
              userID: normalizeId(dutyForm.assessmentStaffId),
              name: assessmentStaffDoc
                ? getStaffDisplayName(assessmentStaffDoc)
                : "",
            }
          : undefined,

      days: isPhysio && dutyForm.requiresAssessment ? 1 : Number(dutyForm.days),
      feesPerDay,
      services,
      slots: dutyForm.slot,
      startDate,
      endDate,
    };

    Object.keys(payload).forEach(
      (k) => payload[k] === undefined && delete payload[k],
    );

    try {
      setAssigning(true);
      const res = await createVisitForPatient(payload);

      if (res?.error) {
        window.alert(res?.message || "Failed to create visit");
        return;
      }
      window.alert("Visit created successfully!");
      handleCancel();
      await fetchPatients();
    } catch (err) {
      console.error("createVisitForPatient error:", err);
      window.alert("Something went wrong while creating visit");
    } finally {
      setAssigning(false);
    }
  };

  /* ---------------- Patient list table ---------------- */

  const filteredData = useMemo(() => {
    let data = Array.isArray(patientList) ? patientList : [];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter((item) => {
        const name = getPatientNameWithTitle(item).toLowerCase();
        const phone = String(
          item?.personalInfo?.basicInfo?.contactInfo?.phone ||
            item?.phone ||
            "",
        ).toLowerCase();
        const id = String(getPatientDisplayId(item) || "").toLowerCase();
        return name.includes(term) || phone.includes(term) || id.includes(term);
      });
    }
    return data;
  }, [patientList, searchTerm]);

  const selectedPatientForHistory = useMemo(
    () => normalizeForReusablePatientHistory(selectedPatientRaw),
    [selectedPatientRaw],
  );

  const handleRowClick = useCallback(
    async (patient) => {
      setSelectedPatientRaw(patient);
      setFormErrors({});
      setStaffError("");
      setAssignStaffList([]);
      setDutyForm(getFreshInitialDutyForm());
      setDischargeSummaries([]);
      setDischargeSummariesError("");

      const patientId = normalizeId(
        patient?._id || patient?.userID || patient?.id,
      );
      if (!patientId) return;

      const requestSeq = ++dischargeFetchSeq.current;
      setDischargeSummariesLoading(true);
      try {
        const res = await getPatientDischargeSummaries(patientId);
        if (requestSeq !== dischargeFetchSeq.current) return;
        if (res && res.error === false) {
          setDischargeSummaries(Array.isArray(res?.data) ? res.data : []);
          return;
        }
        setDischargeSummaries([]);
        setDischargeSummariesError(
          res?.message || "Failed to load discharge summaries",
        );
      } catch (error) {
        if (requestSeq !== dischargeFetchSeq.current) return;
        console.error("getPatientDischargeSummaries error:", error);
        setDischargeSummaries([]);
        setDischargeSummariesError("Failed to load discharge summaries");
      } finally {
        if (requestSeq === dischargeFetchSeq.current) {
          setDischargeSummariesLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleEditClick = useCallback(
    (e, patient) => {
      e.stopPropagation();
      handleRowClick(patient);
      setPatientModalMode("edit");
      setPatientModalTarget(patient);
      setIsAddPatientOpen(true);
    },
    [handleRowClick],
  );

  const patientColumns = useMemo(
    () => [
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Patient Name
          </div>
        ),
        accessor: "name",
        id: "name",
        disableSortBy: false,
        sortType: (rowA, rowB) => {
          const isPatientA = rowA.original?.role === "patient";
          const isPatientB = rowB.original?.role === "patient";

          const piNameA = rowA.original?.personalInfo?.basicInfo?.nameInfo;
          const cpNameA = rowA.original?.contactPersonInfo?.basicInfo?.nameInfo;
          const piNameB = rowB.original?.personalInfo?.basicInfo?.nameInfo;
          const cpNameB = rowB.original?.contactPersonInfo?.basicInfo?.nameInfo;

          const fullNameA =
            (isPatientA
              ? piNameA?.fullName || cpNameA?.fullName
              : piNameA?.fullName) || "";
          const fullNameB =
            (isPatientB
              ? piNameB?.fullName || cpNameB?.fullName
              : piNameB?.fullName) || "";

          return fullNameA.toLowerCase().localeCompare(fullNameB.toLowerCase());
        },
        headerStyle: { textAlign: "left", padding: "2px" },
        style: {
          textAlign: "left",
          padding: "8px",
          whiteSpace: "nowrap",
          overflow: "visible",
        },
        Cell: ({ row, row: { original } }) => {
          const isPatient = original?.role === "patient";
          const piName = original?.personalInfo?.basicInfo?.nameInfo;
          const cpName = original?.contactPersonInfo?.basicInfo?.nameInfo;

          const title =
            (isPatient ? piName?.title || cpName?.title : piName?.title) ||
            "N/A";
          const fullName =
            (isPatient
              ? piName?.fullName || cpName?.fullName
              : piName?.fullName) || "N/A";

          const gender = original?.personalInfo?.basicInfo?.otherInfo?.gender;

          const profileSrc =
            original?.profilePicture ||
            (gender === "male"
              ? isPatient
                ? CommonImages.menwithoutactive
                : original?.active
                  ? CommonImages.menwithactive
                  : CommonImages.menwithoutactive
              : gender === "female"
                ? isPatient
                  ? CommonImages.womenwithoutactive
                  : original?.active
                    ? CommonImages.womenwithactive
                    : CommonImages.womenwithoutactive
                : isPatient
                  ? CommonImages.commonwithoutactive
                  : original?.active
                    ? CommonImages.commonactive
                    : CommonImages.commonwithoutactive);

          const hasContactPerson = Boolean(
            cpName && (cpName?.fullName || cpName?.title),
          );

          return (
            <div className="flex flex-row items-center gap-1.5 whitespace-nowrap ">
              <div className="relative inline-block shrink-0">
                <img
                  src={profileSrc}
                  alt="Profile"
                  className="h-7 w-7 object-cover"
                />
              </div>

              <span
                className="text-blue-600 text-[13px] font-normal"
                title={`${title}. ${fullName}`}
              >
                <p className="max-w-[120px] truncate">
                  {title}. {fullName}
                </p>
              </span>
            </div>
          );
        },
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Gender
          </div>
        ),
        id: "gender",
        accessor: (row) => String(getGender(row) || ""),
        Cell: ({ row: { original } }) => (
          <div className="font-poppins text-[13px] whitespace-nowrap">
            {getGender(original)
              ? capitalizeFirstLetter(getGender(original))
              : "-"}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">Age</div>
        ),
        id: "age",
        accessor: (row) => {
          const dob = getDob(row);
          const m = dob ? moment(dob) : null;
          return m && m.isValid() ? moment().diff(m, "years") : -1;
        },
        Cell: ({ row: { original } }) => (
          <div className="font-poppins text-[13px] whitespace-nowrap">
            {calculateAge(getDob(original))}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Profile
          </div>
        ),
        id: "profile",
        accessor: (row) => {
          if (isPatientProfileComplete(row)) return 6;
          return getProfileCompletionCount(row);
        },
        Cell: ({ row: { original } }) => (
          <div
            className={`font-poppins text-[13px] whitespace-nowrap ml-2 ${
              getProfileStatus(original) === "Completed"
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {original.profileCompletion}
          </div>
        ),
      },

      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px] text-center">
            Reports
          </div>
        ),
        id: "dischargeSummaries",
        accessor: (row) =>
          Array.isArray(row?.dischargeSummaries)
            ? row.dischargeSummaries.length
            : 0,
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          const hasSummary =
            Array.isArray(original?.dischargeSummaries) &&
            original.dischargeSummaries.length > 0;

          return (
            <div className="flex justify-center items-center">
              {hasSummary ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : (
                <Minus className="w-2 h-2 text-gray-400" />
              )}
            </div>
          );
        },
      },
      // {
      //   Header: () => (
      //     <div className="font-poppins whitespace-nowrap text-[13px]">
      //       Location
      //     </div>
      //   ),
      //   id: "location",
      //   accessor: (row) => String(getLocation(row) || ""),
      //   Cell: ({ row: { original } }) => (
      //     <div
      //       className="font-poppins text-blue-600 text-[13px] truncate max-w-[180px]"
      //       title={getLocation(original)}
      //     >
      //       {getLocation(original)}
      //     </div>
      //   ),
      // },
      // {
      //     Header: () => <div className="font-poppins whitespace-nowrap">Onboarded On</div>,
      //     id: "onboardedOn",
      //     accessor: (row) => {
      //         const d = getOnboardedOnRaw(row);
      //         const m = d ? moment(d) : null;
      //         return m && m.isValid() ? m.valueOf() : 0;
      //     },
      //     Cell: ({ row: { original } }) => (
      //         <div className="font-poppins text-[12px] whitespace-nowrap">
      //             {formatOnboardedOn(original)}
      //         </div>
      //     ),
      // },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-center text-[13px]">
            Action
          </div>
        ),
        id: "action",
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          const canPrescribe = isPersonalInfoCompleted(original);

          return (
            <div className="flex justify-center items-center gap-2">
              <button
                onClick={(e) => handleEditClick(e, original)}
                className="flex items-center text-[#3E57E8] hover:text-[#283ca6] hover:bg-blue-50 px-1.5 py-1 rounded transition-colors"
                title="Edit Patient"
              >
                <MdOutlineEdit size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canPrescribe) return;
                  if (!ensurePricingOrPrompt()) return;
                  handleRowClick(original);
                  setPatientModalMode("prescribe");
                  setPatientModalTarget(original);
                  setIsAddPatientOpen(true);
                }}
                disabled={!canPrescribe}
                className={`text-[12px] px-2 py-1 rounded-lg transition-colors ${
                  canPrescribe
                    ? "bg-green-50 text-[#3E57E8] hover:bg-blue-100"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                title={
                  canPrescribe
                    ? "Prescribe"
                    : "Complete personal info to prescribe"
                }
              >
                Prescribe
              </button>
            </div>
          );
        },
      },
    ],
    [
      selectedPatientRaw,
      handleRowClick,
      handleEditClick,
      ensurePricingOrPrompt,
    ],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    setPageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns: patientColumns,
      data: filteredData,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
      },
      autoResetPage: false,
    },
    useSortBy,
    usePagination,
  );

  //   console.log(filteredData);

  useEffect(() => {
    if (pageIndex !== paginationPage) setPaginationPage(pageIndex);
  }, [pageIndex, paginationPage]);

  const handlePageCountChange = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return;
    localStorage.setItem("patientsBookingPageCount", String(n));
    setSelectedPageCount(n);
    setPageSize(n);
    setPaginationPage(0);
    gotoPage(0);
  };

  if (loading) {
    return (
      <div>
        <Loader loading={loading} />
      </div>
    );
  }

  const minDateStr = moment().add(1, "day").format("YYYY-MM-DD");

  return (
    <div
      className="flex flex-col h-[100vh] overflow-y-auto gap-3 overflow-x-hidden font-poppins w-full bg-gray-50 pb-10"
    >
      <div className="flex flex-col min-[1100px]:flex-row items-stretch justify-start w-full min-[1100px]:flex-1 min-[1100px]:min-h-0 h-auto gap-3 p-2 max-w-full">
        {/* Left side */}
        <div className="min-[1100px]:flex-1 flex flex-col gap-3 min-[1100px]:flex-1 min-[1100px]:min-h-0 h-auto w-full min-w-0">
          {/* Patient List */}
          <div
            className={`bg-white rounded-2xl shadow-lg p-2 flex w-full flex-col ${
              selectedPatientRaw ? "flex-1 min-h-0" : "h-full"
            }`}
          >
            <div className="flex justify-between items-center p-2 gap-3">
              <p className="text-[15px] font-semibold font-poppins whitespace-nowrap">
                Patient List ({filteredData.length})
              </p>
              <div className="flex items-center gap-2 ml-auto">
                <div className="flex items-center bg-white border border-blue-600 rounded-md px-2 h-[32px]">
                  <select
                    value={selectedPageCount}
                    onChange={(e) => handlePageCountChange(e.target.value)}
                    className="outline-none font-poppins text-[13px] text-blue-600 bg-transparent"
                  >
                    {[25, 50, 75, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => {
                    if (!ensurePricingOrPrompt()) return;
                    setPatientModalMode("add");
                    setPatientModalTarget(null);
                    setIsAddPatientOpen(true);
                  }}
                  className="px-4 py-1.5 text-[12px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow shadow-[#3E57E8]/20 transition-colors font-poppins"
                >
                  Add Patient
                </button>
              </div>
            </div>

            {error ? (
              <div className="p-3 text-[12px] text-red-600 font-poppins flex-1">
                {error}
              </div>
            ) : filteredData.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1">
                <h2 className="text-[13px] font-semibold font-poppins text-[#787998]">
                  No patients available
                </h2>
              </div>
            ) : (
              <div className="flex-1 overflow-auto mt-1 max-w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                <table {...getTableProps()} className="w-full min-w-[600px]">
                  <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                    {headerGroups.map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        {...headerGroup.getHeaderGroupProps()}
                      >
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps(
                              column.getSortByToggleProps(),
                            )}
                            className={`px-3 py-2 text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap ${
                              column.id === "action"
                                ? "text-center"
                                : "text-left"
                            }`}
                          >
                            <div
                              className={`flex items-center gap-1 ${
                                column.id === "action" ? "justify-center" : ""
                              }`}
                            >
                              {column.render("Header")}
                              {column.canSort && (
                                <SortIcon
                                  isSorted={column.isSorted}
                                  isSortedDesc={column.isSortedDesc}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody
                    {...getTableBodyProps()}
                    className="bg-white divide-y divide-gray-200"
                  >
                    {page.map((row) => {
                      prepareRow(row);
                      const isSelected =
                        normalizeId(selectedPatientRaw?._id) ===
                        normalizeId(row.original?._id);

                      return (
                        <tr
                          key={row.id}
                          {...row.getRowProps()}
                          onClick={() => handleRowClick(row.original)}
                          className={`cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-[#DFEAFF] border-l-4 border-[#3E57E8]"
                              : "hover:bg-gray-50 border-l-4 border-transparent"
                          }`}
                        >
                          {row.cells.map((cell) => (
                            <td
                              key={cell.column.id}
                              {...cell.getCellProps()}
                              className={`px-3 py-2 text-[12px] text-gray-700 whitespace-nowrap ${
                                cell.column.id === "action" ? "text-center" : ""
                              }`}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {filteredData.length > 0 && (
              <div className="mt-2 mb-1">
                <Page
                  paginationPage={paginationPage}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  pageOptions={pageOptions}
                  screen="hospital"
                />
              </div>
            )}
          </div>

          {/* Duty Details Card (UNCHANGED from your current code) */}
        </div>

        {/* Right side */}
        <div className="w-full min-[1100px]:w-[340px] lg:w-[380px] flex-shrink-0 min-w-0 overflow-hidden min-[1100px]:h-full h-auto">
          {!selectedPatientForHistory ? (
            <div className="bg-white rounded-2xl shadow-lg p-4 min-[1100px]:h-full h-auto min-h-[300px] flex flex-col">
              <div className="px-4 py-2 rounded-t-[10px] bg-[#546bb0] flex-shrink-0 mb-2">
                <h3 className="text-[13px] font-medium text-white whitespace-nowrap">
                  Patient Information
                </h3>
              </div>
              <div className="flex-1 border border-gray-100 rounded-b-[10px] flex items-center justify-center bg-gray-50/50">
                <div className="text-[12px] text-gray-500 italic flex flex-col items-center text-center gap-0.5">
                  <span className="font-medium">No Information to display</span>
                  <span>(please select a patient)</span>
                </div>
              </div>
            </div>
          ) : (
            <PatientHistory
              selectedPatient={selectedPatientForHistory}
              dischargeSummaries={dischargeSummaries}
              dischargeSummariesLoading={dischargeSummariesLoading}
              dischargeSummariesError={dischargeSummariesError}
              hideBookingInfo={true}
              showPatientListDetails={true}
            />
          )}
        </div>
      </div>

      {/* {pricingGate.open && (
                <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-3">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl border overflow-hidden">
                        <div className="px-5 py-4 border-b">
                            <h3 className="text-[14px] font-semibold font-poppins text-gray-900">
                                Pricing Required
                            </h3>
                        </div>

                        <div className="px-5 py-4">
                            <p className="text-[12px] text-gray-700 font-poppins leading-5">
                                {pricingGate.message}
                            </p>

                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPricingGate((p) => ({ ...p, open: false }))}
                                    className="px-3 py-1.5 rounded-lg text-[12px] font-poppins bg-gray-100 text-gray-700 hover:bg-gray-200"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setPricingGate((p) => ({ ...p, open: false }));
                                        navigate("/pricing");
                                    }}
                                    className="px-3 py-1.5 rounded-lg text-[12px] font-poppins bg-[#3E57E8] text-white hover:bg-blue-700"
                                >
                                    Go to Pricing
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )} */}

      <AddPatientModal
        isOpen={isAddPatientOpen}
        mode={patientModalMode}
        initialPatient={patientModalTarget}
        existingDischargeSummaries={dischargeSummaries}
        existingDischargeSummariesLoading={dischargeSummariesLoading}
        existingDischargeSummariesError={dischargeSummariesError}
        onClose={() => {
          setIsAddPatientOpen(false);
          setPatientModalTarget(null);
          setPatientModalMode("add");
        }}
        onSave={async (payload, options = {}) => {
          //   console.log("Add Patient Payload:", payload);
          try {
            const { suppressSuccessToast = false, suppressErrorToast = false } =
              options || {};
            const isBulk = payload?.type === "bulk";
            const isEdit =
              patientModalMode === "edit" ||
              Boolean(payload?._id || payload?.id || payload?.userID);
            const res = isEdit
              ? await updatePatient(payload)
              : await addPatient(payload);
            if (res && res.error === false) {
              if (!suppressSuccessToast) {
                if (isBulk) {
                  showToast(
                    res?.message || "Patients uploaded successfully!",
                    "Success",
                  );
                } else {
                  showToast(
                    isEdit
                      ? "Patient updated successfully!"
                      : "Patient added successfully!",
                    "Success",
                  );
                }
              }
              fetchPatients(true); // Silent refresh
              return res;
            } else {
              const fallbackErrorMessage = isBulk
                ? "Failed to upload patients."
                : isEdit
                  ? "Failed to update patient."
                  : "Failed to add patient.";
              const errorMessage = res?.message || fallbackErrorMessage;
              if (!suppressErrorToast) {
                showToast(errorMessage, "Error");
              }
              return (
                res || {
                  error: true,
                  message: errorMessage,
                }
              );
            }
          } catch (err) {
            console.error("Error adding patient:", err);
            const errorMessage =
              err?.response?.data?.message ||
              err?.message ||
              "An unexpected error occurred.";
            if (!options?.suppressErrorToast) {
              showToast(errorMessage, "Error");
            }
            return {
              error: true,
              message: errorMessage,
            };
          }
        }}
        onUploadDischargeSummary={async (
          { patientID, title, file },
          options = {},
        ) => {
          try {
            const { finalizeAddFlow = false, suppressErrorToast = false } =
              options || {};
            if (!patientID) {
              if (!suppressErrorToast) {
                showToast(
                  "MRN is missing. Please save profile details in Step 1.",
                  "Error",
                );
              }
              return false;
            }
            const res = await uploadDischargeSummary({
              patientID,
              title,
              file,
            });
            if (res && res.error === false) {
              showToast(
                finalizeAddFlow
                  ? "Patient and discharge summary saved successfully!"
                  : "Discharge summary uploaded successfully!",
                "Success",
              );
              return res;
            }
            if (!suppressErrorToast) {
              showToast(
                res?.message || "Failed to upload discharge summary.",
                "Error",
              );
            }
            return false;
          } catch (err) {
            console.error("Error uploading discharge summary:", err);
            if (!options?.suppressErrorToast) {
              showToast(
                "An unexpected error occurred while uploading document.",
                "Error",
              );
            }
            return false;
          }
        }}
      />
    </div>
  );
};

export default PatientsBooking;